import { useTranslation } from '@/contexts/i18n-context';
import { Loader } from '@/components/loaders/loader';
import styles from './person-information-section.module.css';
import clsx from 'clsx';
import { Button } from '../button';
import { useLoggedUser } from '@/contexts/current-user-context';
import { PersonDetailsSection } from './person-details-section';
import { PersonAttributesSection } from './person-attributes-section';
import { useUpdatePatientSurrogate } from '@/api/update-surrogate-info';
import { GetPersonalInfoResponseSurrogate } from '@/api/api.schemas';

type SurrogateType = 'mandatory' | 'optional';

interface SurrogateInformationProps {
  surrogate: GetPersonalInfoResponseSurrogate | undefined;
  isFetchingSurrogate: boolean;
  onUpdate: () => void;
  type: SurrogateType;
  isPatientCompleted: boolean;
  canEdit: boolean;
}

export function SurrogateInformation({
  surrogate,
  isFetchingSurrogate,
  onUpdate,
  type,
  isPatientCompleted,
  canEdit,
}: SurrogateInformationProps) {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { mutate: deleteSurrogate, isPending: isDeletingSurrogate } = useUpdatePatientSurrogate();

  const onDelete = async () => {
    deleteSurrogate(
      { patientId: user.id, data: { surrogate: null } },
      {
        onSuccess: () => {
          onUpdate();
        },
      },
    );
  };

  if (isFetchingSurrogate) {
    return <Loader type="small" />;
  }

  return (
    <div className={styles.section}>
      <div className={styles.titleSection}>
        <p className={clsx('body-1', 'bold')}>{t('patientInformation.surrogate.title')}</p>
        {surrogate && canEdit && (
          <Button variant="link" size="S" leftIcon="edit" as="a" to="/profile/edit-surrogate">
            {t('patientInformation.editDetails')}
          </Button>
        )}
      </div>
      <div className={styles.detailsContainer}>
        {surrogate ? (
          <>
            <PersonDetailsSection
              variant="surrogate"
              person={{ ...surrogate, dateOfBirth: surrogate.birthday }}
              relationshipToPatient={surrogate.relationshipToPatient}
            />

            <PersonAttributesSection
              address1={surrogate.address1}
              address2={surrogate.address2}
              city={surrogate.city}
              state={surrogate.state}
              zip={surrogate.zip}
              email={surrogate.email}
              cellPhone={surrogate.phone}
            />

            <Button
              variant="outlined"
              size="M"
              disabled={!canEdit}
              onClick={onDelete}
              className={styles.removeBtn}
              loading={isDeletingSurrogate}
            >
              {t('patientInformation.surrogate.removeBtn')}
            </Button>
          </>
        ) : (
          <SurrogateInformationEmptySection
            type={type}
            isPatientCompleted={isPatientCompleted}
            canEdit={canEdit}
          />
        )}
      </div>
    </div>
  );
}

interface SurrogateInformationEmptySectionProps {
  type: SurrogateType;
  isPatientCompleted: boolean;
  canEdit: boolean;
}

function SurrogateInformationEmptySection({
  type,
  isPatientCompleted,
  canEdit,
}: SurrogateInformationEmptySectionProps) {
  const { t } = useTranslation();

  if (type === 'optional') {
    const addSurrogatePath = isPatientCompleted
      ? '/profile/edit-surrogate'
      : '/profile/type-selection';

    return (
      <div className={styles.emptySectionOptional}>
        <p>{t('patientInformation.emptySection.surrogateOptional.title')}</p>
        <Button variant="outlined" size="M" as="a" to={addSurrogatePath} disabled={!canEdit}>
          {t('patientInformation.emptySection.surrogateOptional.buttonTxt')}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.emptySection}>
      <p className={clsx('body-1', 'bold')}>
        {t('patientInformation.emptySection.surrogate.title')}
      </p>
      <Button size="M" as="a" to="/profile/surrogate" disabled={!canEdit}>
        {t('patientInformation.emptySection.surrogate.buttonTxt')}
      </Button>
    </div>
  );
}
